import { useEffect } from 'react';
import { useStore } from '../../store';
import { NEW_MESSAGE_WINDOW } from '../../store/slices/chatWindows';
import {
  collapseDockAndFocusBar,
  focusDockSearch,
  isTextEntry,
  minimizeAndFocusTab,
  openDockAndFocusSearch,
} from './chatFocus';
import { useOpenChatFromChart } from './useOpenChatFromChart';

/** Esc in the phone sheet closes the innermost layer. */
function closeSheetLayer(): void {
  const state = useStore.getState();
  const id = state.chatWindows.open[0];
  const inConversation = state.chatSheetView === 'conversation' && !!id;
  if (
    inConversation &&
    (state.windowViews[id] ?? 'conversation') !== 'conversation'
  )
    state.setChatWindowView(id, 'conversation');
  else if (inConversation) state.setChatSheetView('inbox');
  else state.setChatSheetOpen(false);
}

/** ArrowUp / ArrowDown between the search field and the dock rows. */
function moveInDock(dock: Element, active: Element, key: string): boolean {
  const rows = Array.from(
    dock.querySelectorAll<HTMLElement>('[data-chat-row]')
  );
  const at = rows.indexOf(active as HTMLElement);
  const inSearch = active.hasAttribute('data-chat-search');
  if (at === -1 && !inSearch) return false;
  if (key === 'ArrowDown') {
    // From the search field the first row; from a row the next, never past the last.
    rows[Math.min(at + 1, rows.length - 1)]?.focus();
    return true;
  }
  if (inSearch) return false;
  if (at === 0) focusDockSearch();
  else rows[at - 1].focus();
  return true;
}

/**
 * The chat keyboard map, mounted once by the tray: Cmd/Ctrl+. toggles chat from
 * anywhere; Esc, N and the arrows act only while focus is inside a chat surface,
 * so a key typed in a chart control is never chat's.
 */
export function useChatKeyboard(): void {
  const openFromChart = useOpenChatFromChart();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement;

      if (e.key === '.' && (e.metaKey || e.ctrlKey) && !e.altKey) {
        e.preventDefault();
        const state = useStore.getState();
        if (state.chatLayout === 'sheet') {
          // The sheet returns focus to its pill when it closes.
          if (state.chatSheetOpen) state.setChatSheetOpen(false);
          else openFromChart();
        } else if (state.chatWindows.dockOpen) {
          if (active?.closest('[data-chat-dock]')) collapseDockAndFocusBar();
          else state.setChatDockOpen(false);
        } else {
          openDockAndFocusSearch(openFromChart);
        }
        return;
      }

      // Radix menus call preventDefault on the Escape they consume, so a
      // prevented Escape belongs to the menu. The attachment lightbox stops its
      // own Escape before it reaches this listener.
      if (e.defaultPrevented || e.isComposing || !active) return;

      if (e.key === 'Escape') {
        const win = active.closest<HTMLElement>('[data-chat-window]');
        if (win?.dataset.chatWindow)
          minimizeAndFocusTab(win.dataset.chatWindow);
        else if (active.closest('[data-chat-dock="open"]'))
          collapseDockAndFocusBar();
        else if (active.closest('[data-chat-sheet="dialog"]'))
          closeSheetLayer();
        return;
      }

      const dock = active.closest('[data-chat-dock="open"]');
      const inboxSheet =
        active.closest('[data-chat-sheet="dialog"]') &&
        useStore.getState().chatSheetView === 'inbox';
      if (!dock && !inboxSheet) return;
      if (
        (e.key === 'n' || e.key === 'N') &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isTextEntry(active)
      ) {
        // Prevented so the autofocused To field does not receive the "n".
        e.preventDefault();
        useStore.getState().openChatWindow(NEW_MESSAGE_WINDOW);
      } else if (
        dock &&
        (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
        moveInDock(dock, active, e.key)
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openFromChart]);
}
